import { CheckCircle2, XCircle } from "lucide-react";

/* ---------- Shared article building blocks ---------- */

export function H2({ children }) {
  return (
    <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-[#0A192F] leading-tight">
      {children}
    </h2>
  );
}

export function P({ children }) {
  return <p className="text-base md:text-lg leading-[1.75] text-slate-700 my-4">{children}</p>;
}

export function Pull({ children }) {
  return (
    <div className="border-l-4 border-[#C9A349] bg-[#FAF6EC] px-5 md:px-7 py-5 my-8 text-base md:text-lg text-slate-700 leading-relaxed">
      {children}
    </div>
  );
}

export function Callout({ tone = "ink", icon: Icon, kicker, children }) {
  const tones = {
    ink: "border-[#0A192F] bg-[#0A192F] text-white",
    warning: "border-[#dc2626] bg-[#fff7f7] text-slate-800",
    success: "border-[#16a34a] bg-[#f0fdf4] text-slate-800",
    gold: "border-[#C9A349] bg-[#FAF6EC] text-slate-800",
  };
  const kickerColor = {
    ink: "text-[#C9A349]",
    warning: "text-[#dc2626]",
    success: "text-[#15803d]",
    gold: "text-[#7c5d10]",
  };
  return (
    <div className={`my-10 border-l-4 ${tones[tone]} p-6 md:p-7`}>
      <div
        className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold ${kickerColor[tone]}`}
      >
        {Icon && <Icon className="w-4 h-4" />}
        {kicker}
      </div>
      <div
        className={`mt-3 ${
          tone === "ink" ? "text-slate-100" : "text-slate-800"
        } text-base md:text-lg leading-relaxed`}
      >
        {children}
      </div>
    </div>
  );
}

export function ExampleTable({ rows, footnote }) {
  return (
    <div className="my-8 border border-slate-200 bg-slate-50">
      <table className="w-full text-sm md:text-base">
        <tbody>
          {rows.map(([label, value], i) => {
            const isLast = i === rows.length - 1;
            return (
              <tr
                key={label}
                className={`${
                  isLast ? "bg-[#0A192F] text-white" : i % 2 ? "bg-white" : "bg-slate-50"
                }`}
              >
                <td className="px-5 py-3 border-b border-slate-200">
                  <span className={isLast ? "font-heading font-bold" : ""}>{label}</span>
                </td>
                <td
                  className={`px-5 py-3 border-b border-slate-200 text-right tabular-nums ${
                    isLast ? "font-heading font-black text-[#C9A349]" : "font-medium"
                  }`}
                >
                  {value}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {footnote && (
        <div className="px-5 py-3 text-xs text-slate-500 leading-relaxed border-t border-slate-200 bg-white">
          {footnote}
        </div>
      )}
    </div>
  );
}

export function RolePair({ left, right }) {
  // Two side-by-side comparison cards, used to contrast two GST treatments,
  // two pricing approaches, etc.
  return (
    <div className="grid md:grid-cols-2 gap-4 my-8">
      <ComparisonCard {...left} />
      <ComparisonCard {...right} />
    </div>
  );
}

function ComparisonCard({ accent, kicker, title, lines, verdict }) {
  const accentClass = {
    green: "border-emerald-500 bg-emerald-50",
    red: "border-red-500 bg-red-50",
    ink: "border-[#0A192F] bg-slate-50",
    gold: "border-[#C9A349] bg-[#FAF6EC]",
  }[accent || "ink"];
  const verdictColor = {
    green: "text-emerald-600",
    red: "text-red-600",
    ink: "text-[#0A192F]",
    gold: "text-[#7c5d10]",
  }[accent || "ink"];
  return (
    <div className={`border-l-4 ${accentClass} p-6 md:p-7 flex flex-col`}>
      <div className={`text-[11px] uppercase tracking-[0.22em] font-bold ${verdictColor}`}>
        {kicker}
      </div>
      <h3 className="mt-2 font-heading font-black text-lg md:text-xl text-[#0A192F] leading-tight">
        {title}
      </h3>
      <ul className="mt-4 space-y-2 text-sm md:text-base text-slate-700 flex-1">
        {lines.map((l, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="w-1.5 h-1.5 mt-2 bg-[#C9A349] flex-shrink-0" />
            <span className="leading-relaxed">{l}</span>
          </li>
        ))}
      </ul>
      {verdict && (
        <div
          className={`mt-5 pt-4 border-t border-slate-200 text-sm font-heading font-bold uppercase tracking-[0.12em] ${verdictColor}`}
        >
          {verdict}
        </div>
      )}
    </div>
  );
}

export function RulesGrid({ rules }) {
  return (
    <div className="grid md:grid-cols-3 gap-4 my-8">
      {rules.map(({ n, title, body }) => (
        <div key={n} className="border border-slate-200 bg-white p-6 flex flex-col">
          <div className="font-heading font-black text-4xl text-[#C9A349] tracking-tight">{n}</div>
          <div className="mt-3 font-heading font-bold text-[#0A192F]">{title}</div>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{body}</p>
        </div>
      ))}
    </div>
  );
}

export function Li({ tone, children }) {
  const Icon = tone === "good" ? CheckCircle2 : XCircle;
  const color = tone === "good" ? "text-emerald-600" : "text-red-500";
  return (
    <li className="flex items-start gap-3 text-base md:text-lg text-slate-700 leading-relaxed">
      <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${color}`} />
      <span>{children}</span>
    </li>
  );
}

export function BigRule({ green, red }) {
  // The "this is the headline takeaway" panel — black background, gold text.
  return (
    <div className="my-12 border border-slate-200 bg-[#0A192F] text-white p-8 md:p-10">
      <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#C9A349]">
        The simple rule
      </div>
      <div className="mt-6 grid md:grid-cols-2 gap-6 md:gap-8">
        <div className="border-l-2 border-emerald-400 pl-5">
          <div className="text-emerald-300 text-xs uppercase tracking-[0.2em] font-bold">
            {green.label}
          </div>
          <div className="mt-2 font-heading font-black text-3xl md:text-4xl tracking-tight leading-[1.05]">
            {green.value}
          </div>
        </div>
        <div className="border-l-2 border-red-400 pl-5">
          <div className="text-red-300 text-xs uppercase tracking-[0.2em] font-bold">
            {red.label}
          </div>
          <div className="mt-2 font-heading font-black text-3xl md:text-4xl tracking-tight leading-[1.05]">
            {red.value}
          </div>
        </div>
      </div>
    </div>
  );
}
